export function callSafe(fn: (...args: any[]) => any): void {
	try {
		fn();
	} catch {
		// Do nothing
	}
}

export function debounce<
	// eslint-disable-next-line space-before-function-paren, function-paren-newline
	F extends (...args: any) => any,
	A extends Parameters<F>,
>(
	fn: F,
	wait: number,
): (...args: A) => void {
	let timeout: NodeJS.Timeout | undefined;

	return (...args: A): void => {
		if (!timeout) {
			fn(...args);
			timeout = setTimeout(
				() => {
					timeout = undefined;
				},
				wait,
			);
			return;
		}

		clearTimeout(timeout);
		timeout = setTimeout(
			() => {
				fn(...args);
				timeout = undefined;
			},
			wait,
		);
	};
}

export function promiseDebounce<
	// eslint-disable-next-line space-before-function-paren, function-paren-newline
	F extends (...args: any[]) => Promise<any>,
	A extends Parameters<F>,
>(
	fn: F,
	wait: number,
): (...args: A) => Promise<void> {
	let timeout: NodeJS.Timeout | undefined;

	return (...args: A): Promise<void> => new Promise((resolve) => {
		if (!timeout) {
			fn(...args).then(resolve);
			timeout = setTimeout(
				() => {
					timeout = undefined;
				},
				wait,
			);
			return;
		}

		clearTimeout(timeout);
		timeout = setTimeout(
			() => {
				fn(...args).then(resolve);
				timeout = undefined;
			},
			wait,
		);
	});
}

export function requestAnimationFrameDebounce<
	// eslint-disable-next-line space-before-function-paren, function-paren-newline
	F extends (...args: any) => any,
	A extends Parameters<F>,
>(fn: F): (...args: A) => void {
	let raf: number | undefined;

	return (...args: A): void => {
		if (raf) {
			cancelAnimationFrame(raf);
		}

		raf = requestAnimationFrame(() => {
			fn(...args);
			raf = undefined;
		});
	};
}
